import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Monitor, Ban, RefreshCw, ShieldCheck, ShieldAlert } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function summariseUA(ua) {
  if (!ua) return "Unknown";
  const s = ua.toLowerCase();
  let browser = "Browser";
  if (s.includes("edg/")) browser = "Edge";
  else if (s.includes("chrome/") && !s.includes("edg/")) browser = "Chrome";
  else if (s.includes("firefox/")) browser = "Firefox";
  else if (s.includes("safari/") && !s.includes("chrome/")) browser = "Safari";
  else if (s.includes("curl/")) browser = "curl";
  else if (s.includes("postman")) browser = "Postman";
  let os = "";
  if (s.includes("windows")) os = "Windows";
  else if (s.includes("mac os x") || s.includes("macintosh")) os = "macOS";
  else if (s.includes("android")) os = "Android";
  else if (s.includes("iphone") || s.includes("ipad")) os = "iOS";
  else if (s.includes("linux")) os = "Linux";
  return os ? `${browser} · ${os}` : browser;
}

/**
 * Admin-only dialog listing recent sessions (active + revoked) for a user.
 * Data source:
 *   GET  /api/users/{id}/sessions
 *   POST /api/users/{id}/sessions/{sid}/revoke
 */
export default function SessionActivityDialog({ user, open, onOpenChange, onRevoked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/users/${user.id}/sessions`);
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load sessions"));
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const revoke = async (sid) => {
    setRevokingId(sid);
    try {
      await api.post(`/api/users/${user.id}/sessions/${sid}/revoke`);
      toast.success("Session revoked");
      await load();
      onRevoked?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to revoke session"));
    } finally {
      setRevokingId(null);
    }
  };

  const sessions = data?.sessions || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="session-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-brand" /> Session activity
            <span className="text-sm text-slate-500 font-normal">· {user?.name || user?.email}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-slate-500 uppercase tracking-wide text-[10px]">Last sign-in</div>
            <div className="mt-1 font-medium text-slate-800" data-testid="session-last-login">
              {fmtDate(data?.user?.last_login_at)}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-slate-500 uppercase tracking-wide text-[10px]">Last IP</div>
            <div className="mt-1 font-medium text-slate-800 font-mono" data-testid="session-last-ip">
              {data?.user?.last_login_ip || "—"}
            </div>
          </div>
          <div className="rounded-md border border-slate-200 p-3">
            <div className="text-slate-500 uppercase tracking-wide text-[10px]">Active sessions</div>
            <div className="mt-1 font-medium text-slate-800 flex items-center gap-1.5" data-testid="session-active-count">
              {(data?.active_count ?? 0) > 0 ? (
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <ShieldAlert className="w-3.5 h-3.5 text-slate-400" />
              )}
              {data?.active_count ?? 0}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 mb-2">
          <div className="text-sm font-medium text-slate-700">Recent sessions</div>
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            disabled={loading}
            data-testid="session-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {sessions.length === 0 && (
          <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-md p-4 text-center">
            No sessions recorded yet.
          </div>
        )}

        <div className="space-y-2">
          {sessions.map((s) => {
            const isRevoked = !!s.revoked_at;
            return (
              <div
                key={s.id}
                data-testid={`session-row-${s.id}`}
                className={`rounded-md border p-3 flex items-start justify-between gap-3 ${
                  isRevoked ? "border-slate-200 bg-slate-50" : "border-emerald-200 bg-emerald-50/40"
                }`}
              >
                <div className="flex-1 text-xs space-y-0.5">
                  <div className="font-medium text-slate-800 flex items-center gap-2">
                    {summariseUA(s.user_agent)}
                    {isRevoked ? (
                      <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-700 rounded px-1.5 py-0.5">
                        revoked
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">
                        active
                      </span>
                    )}
                  </div>
                  <div className="text-slate-600">
                    IP <span className="font-mono">{s.ip || "—"}</span>
                    {" · "}Started {fmtDate(s.created_at)}
                    {" · "}Last seen {fmtDate(s.last_seen_at)}
                  </div>
                  {isRevoked && (
                    <div className="text-slate-500 text-[11px]">
                      Revoked {fmtDate(s.revoked_at)}
                    </div>
                  )}
                  <div className="text-slate-400 font-mono text-[10px] truncate" title={s.user_agent}>
                    {s.user_agent || ""}
                  </div>
                </div>
                {!isRevoked && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => revoke(s.id)}
                    disabled={revokingId === s.id}
                    className="text-rose-700 border-rose-200 hover:bg-rose-50"
                    data-testid={`revoke-session-${s.id}`}
                  >
                    <Ban className="w-3 h-3 mr-1" />
                    {revokingId === s.id ? "Revoking…" : "Revoke"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-[11px] text-slate-500 mt-3 border-t border-slate-100 pt-2">
          Note: revoking blocks future token renewals. An outstanding access token
          may keep working until it naturally expires (~12h). For immediate lock-out,
          also deactivate the user.
        </div>
      </DialogContent>
    </Dialog>
  );
}
